import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send, Eye, X, Users, Search, Image, FileText, CheckCircle, AlertCircle, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppBroadcast = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  
  // WhatsApp Settings
  const [whatsappSettings, setWhatsappSettings] = useState({
    is_configured: false,
    connection_status: 'not_configured',
    whatsapp_number: ''
  });
  
  // Progress modal
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');
  
  // Search params
  const [searchParams, setSearchParams] = useState({
    magazine: 'Aabkari Times',
    recipient_type: 'both',
    status: 'ACTIVE'
  });
  
  // Message form
  const [formData, setFormData] = useState({
    campaign_type: 'magazine',
    campaign_name: '',
    message: '',
    media_url: ''
  });
  
  // Message templates
  const templates = {
    magazine: `🎉 *{magazine} - Latest Edition* 🎉

Dear {name},

We are pleased to share the latest edition of {magazine}. 

📖 Read Now: {read_link}
📥 Download PDF: {pdf_link}

Thank you for your continued support!

Best Regards,
Team {magazine}`,
    renewal: `📋 *Membership Renewal Reminder*

Dear {name},

Your {magazine} membership is expiring soon.

🗓️ Expiry Date: {expiry_date}
💰 Renewal Amount: ₹{amount}

Renew now to continue receiving our magazine:
🔗 {renewal_link}

Thank you!
Team {magazine}`,
    promotional: `✨ *Special Offer from {magazine}* ✨

Dear {name},

{promo_message}

📞 Contact us for more details!

Best Regards,
Team {magazine}`
  };
  
  useEffect(() => {
    fetchWhatsAppSettings();
  }, []);
  
  const fetchWhatsAppSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhatsappSettings(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    }
  };
  
  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/whatsapp/recipients`, searchParams, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data || []);
      setSelectedRecipients((response.data || []).map(r => r.id));
    } catch (error) {
      console.error('Error fetching recipients:', error);
      toast.error('Failed to fetch recipients');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRecipients(recipients.map(r => r.id));
    } else {
      setSelectedRecipients([]);
    }
  };
  
  const handleSelectRecipient = (id, checked) => {
    if (checked) {
      setSelectedRecipients([...selectedRecipients, id]);
    } else {
      setSelectedRecipients(selectedRecipients.filter(r => r !== id));
    }
  };
  
  const handleTemplateChange = (type) => {
    setFormData(prev => ({
      ...prev,
      campaign_type: type,
      message: templates[type] || ''
    }));
  };
  
  const handleSendBulk = async () => {
    const selectedData = recipients.filter(r => selectedRecipients.includes(r.id));
    
    if (selectedData.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    setShowPreview(false);
    
    try {
      const token = localStorage.getItem('token');
      const jobId = `wa_${Date.now()}`;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      const response = await axios.post(`${API}/whatsapp/send-bulk`, {
        recipients: selectedData,
        message: formData.message,
        media_url: formData.media_url || null,
        campaign_type: formData.campaign_type,
        campaign_name: formData.campaign_name || `WhatsApp ${formData.campaign_type} - ${new Date().toLocaleDateString()}`,
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`WhatsApp messages sent: ${response.data.sent_count} successful, ${response.data.failed_count} failed`);
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp messages');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };
  
  const handleTestMessage = async () => {
    const testPhone = prompt('Enter your WhatsApp number (with country code, e.g., +919876543210):');
    if (!testPhone) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/whatsapp/send-text`, {
        phone: testPhone,
        message: formData.message || 'This is a test message from your app!'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Test message sent! Check your WhatsApp.');
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={20} />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <MessageCircle className="mr-3 text-green-600" size={28} />
                WhatsApp Broadcast
              </h1>
              <p className="text-gray-600">Send WhatsApp messages to members and advertisers</p>
            </div>
          </div>
          
          {/* Connection Status */}
          <div className={`flex items-center px-4 py-2 rounded-lg ${
            whatsappSettings.connection_status === 'connected' 
              ? 'bg-green-100 text-green-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {whatsappSettings.connection_status === 'connected' ? (
              <>
                <CheckCircle size={18} className="mr-2" />
                Connected ({whatsappSettings.whatsapp_number || 'Sandbox'})
              </>
            ) : (
              <>
                <AlertCircle size={18} className="mr-2" />
                {whatsappSettings.connection_status === 'not_configured' ? 'Not Configured' : 'Connection Error'}
              </>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-6">
          {/* Left - Recipients */}
          <div className="col-span-2 space-y-6">
            {/* Search Recipients */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Users className="mr-2 text-green-600" size={20} />
                Select Recipients
              </h2>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <select
                    value={searchParams.magazine}
                    onChange={(e) => setSearchParams({ ...searchParams, magazine: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Aabkari Times">Aabkari Times</option>
                    <option value="Sugar Times">Sugar Times</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Recipient Type</Label>
                  <select
                    value={searchParams.recipient_type}
                    onChange={(e) => setSearchParams({ ...searchParams, recipient_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="both">Members & Advertisers</option>
                    <option value="members">Members Only</option>
                    <option value="advertisers">Advertisers Only</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    value={searchParams.status}
                    onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="ACTIVE">Active Only</option>
                    <option value="ALL">All</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button 
                    onClick={fetchRecipients} 
                    className="w-full"
                    disabled={loading}
                    style={{ backgroundColor: '#25D366' }}
                  >
                    <Search className="mr-2" size={18} />
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                </div>
              </div>
              
              {/* Recipients Stats */}
              {recipients.length > 0 && (
                <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg mb-4">
                  <div className="flex items-center space-x-6 text-sm">
                    <span className="font-semibold text-green-700">
                      {recipients.length} Recipients with Phone Numbers
                    </span>
                    <span className="text-green-600">
                      {selectedRecipients.length} Selected
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedRecipients.length === recipients.length && recipients.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="text-sm">Select All</span>
                  </div>
                </div>
              )}
              
              {/* Recipients Table */}
              {recipients.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left w-10"></th>
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">Phone</th>
                        <th className="p-2 text-left">Company</th>
                        <th className="p-2 text-left">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map(recipient => (
                        <tr key={recipient.id} className="border-t hover:bg-gray-50">
                          <td className="p-2">
                            <Checkbox
                              checked={selectedRecipients.includes(recipient.id)}
                              onCheckedChange={(checked) => handleSelectRecipient(recipient.id, checked)}
                            />
                          </td>
                          <td className="p-2 font-medium">{recipient.name}</td>
                          <td className="p-2 text-green-600">{recipient.phone}</td>
                          <td className="p-2 text-gray-600">{recipient.company}</td>
                          <td className="p-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              recipient.type === 'member' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {recipient.type}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {recipients.length === 0 && !loading && (
                <p className="text-center text-gray-500 py-8">
                  Click "Search" to find recipients with phone numbers
                </p>
              )}
            </div>
          </div>
          
          {/* Right - Message Composer */}
          <div className="space-y-6">
            {/* Message Template */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <FileText className="mr-2 text-green-600" size={20} />
                Compose Message
              </h2>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Campaign Type</Label>
                  <select
                    value={formData.campaign_type}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="magazine">Monthly Magazine</option>
                    <option value="renewal">Renewal Reminder</option>
                    <option value="promotional">Promotional</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Campaign Name</Label>
                  <Input
                    value={formData.campaign_name}
                    onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                    placeholder="e.g., January 2026 Magazine"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Message *</Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={10}
                    placeholder="Enter your WhatsApp message..."
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500">
                    Use *text* for bold, _text_ for italic. Variables like {'{name}'} will be replaced.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Media URL (Optional)</Label>
                  <Input
                    value={formData.media_url}
                    onChange={(e) => setFormData({ ...formData, media_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500">
                    Image (JPG, PNG) or PDF link
                  </p>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6 space-y-3">
              <Button
                onClick={handleTestMessage}
                variant="outline"
                className="w-full"
                disabled={!formData.message}
              >
                <MessageCircle className="mr-2" size={18} />
                Send Test Message
              </Button>
              
              <Button
                onClick={() => setShowPreview(true)}
                className="w-full"
                style={{ backgroundColor: '#25D366' }}
                disabled={selectedRecipients.length === 0 || !formData.message}
              >
                <Eye className="mr-2" size={18} />
                Preview & Send ({selectedRecipients.length})
              </Button>
            </div>
          </div>
        </div>
        
        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
              <div className="bg-green-600 text-white p-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center">
                  <MessageCircle className="mr-2" size={20} />
                  WhatsApp Preview
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)} className="text-white hover:bg-green-700">
                  <X size={20} />
                </Button>
              </div>
              
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Phone mockup */}
                <div className="bg-[#e5ddd5] rounded-lg p-4 max-w-md mx-auto">
                  <div className="bg-white rounded-lg p-3 shadow">
                    <p className="whitespace-pre-wrap text-sm">{formData.message}</p>
                    {formData.media_url && (
                      <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
                        <Image size={14} className="inline mr-1" />
                        Media: {formData.media_url}
                      </div>
                    )}
                    <p className="text-right text-xs text-gray-400 mt-2">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                
                <div className="text-center text-sm text-gray-600">
                  This message will be sent to <strong>{selectedRecipients.length}</strong> recipients
                </div>
              </div>
              
              <div className="bg-gray-100 p-4 flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSendBulk}
                  disabled={sending}
                  style={{ backgroundColor: '#25D366' }}
                >
                  {sending ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Send to {selectedRecipients.length} Recipients
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Progress Modal - reuse existing component with custom polling */}
        {showProgressModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <MessageCircle className="mr-2 text-green-600" size={20} />
                Sending WhatsApp Messages
              </h3>
              <div className="space-y-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: '100%' }}
                  />
                </div>
                <p className="text-center text-sm text-gray-600">
                  Processing messages... This may take a few minutes.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => setShowProgressModal(false)}
                >
                  Close (Sending continues in background)
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppBroadcast;
